import React from 'react';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import { BLOCKS, MARKS } from '@contentful/rich-text-types';

import { ScrollReveal } from '../../ScrollReveal';
import RichText from '../../RichText';
import { EmbeddedEntryNode } from '../../RichText/Nodes';

import { TableProps } from '../shared';

interface BasicTableProps extends TableProps {
  basic?: boolean;
}

export function BasicTable({
  title,
  subtitle,
  tableId,
  tableBody,
  tableFootnotes,
  basic = true
}: BasicTableProps) {
  const RICH_TEXT_OVERRIDES: RenderNode = {
    [BLOCKS.TABLE]: (_node, children) => (
      <table
        className={`basic-table w-full border-collapse ${
          !basic
            ? '[&:nth-last-of-type(1)]:bg-transparent table-fixed [&:nth-last-of-type(1)_p]:mb-[1.3125rem] [&:nth-of-type(even)]:bg-[#f1f1f1] [&:nth-of-type(odd)_td:nth-of-type(1)]:w-[35%] desktop:[&:nth-of-type(odd)_td:nth-of-type(1)]:w-[35%] [&:nth-of-type(odd)_td:nth-of-type(2)]:w-[20%] desktop:[&:nth-of-type(odd)_td:nth-of-type(2)]:w-[15%] [&:nth-of-type(odd)_td:nth-of-type(3)]:w-[45%] desktop:[&:nth-of-type(odd)_td:nth-of-type(3)]:w-[50%]'
            : ''
        }`}
      >
        <tbody>{children}</tbody>
      </table>
    ),
    [BLOCKS.TABLE_ROW]: (_node, children) => (
      <tr className="basic-row [&_p]:m-0">{children}</tr>
    ),
    [BLOCKS.TABLE_HEADER_CELL]: (_node, children) => (
      <th
        className={`basic-header bg-[black] p-[0.9375rem] text-left font-normal text-white ${
          !basic
            ? '[&:nth-of-type(1)]:w-[35%] desktop:[&:nth-of-type(1)]:w-[35%] [&:nth-of-type(2)]:w-[20%] desktop:[&:nth-of-type(2)]:w-[15%] [&:nth-of-type(3)]:w-[45%] desktop:[&:nth-of-type(3)]:w-[50%]'
            : ''
        }`}
        data-testid="basic-header"
      >
        {children}
      </th>
    ),
    [BLOCKS.TABLE_CELL]: (_node, children) => (
      <td className="basic-cell border border-lighter-gray p-[0.9375rem] [&_p]:break-words">
        {children}
      </td>
    ),
    [BLOCKS.PARAGRAPH]: (_node, children) => (
      <p className="mb-4 text-sm font-light leading-[1.75rem] desktop:text-[1.125rem] [&_a]:text-navy [&_a_span]:font-semibold [&_span]:font-semibold">
        {children}
      </p>
    ),
    [BLOCKS.EMBEDDED_ENTRY]: (node, children) => (
      <div className="mx-0 my-[2.25rem] flex">
        <EmbeddedEntryNode node={node} colour="darkButton">
          {children}
        </EmbeddedEntryNode>
      </div>
    )
  };

  return (
    <ScrollReveal>
      <div
        className="block-container"
        id={tableId!}
        data-blockid="basic"
        data-testid="basic-table"
      >
        <div className="-mx-2 box-border flex w-full flex-1 flex-wrap justify-center pt-8 tablet:-mx-3 tablet:pt-24 desktop:-mx-4">
          <div className="flex w-full shrink-0 grow-0 flex-col px-2 tablet:w-2/3 tablet:px-3 desktop:px-4">
            {title && (
              <p className="mx-0 my-[2.25rem] text-[1.75rem] font-semibold desktop:text-[2.625rem]">
                {title}
              </p>
            )}

            {subtitle && (
              <p className="mb-[2.25rem] text-[1.75rem] font-semibold desktop:text-[2.625rem]">
                {subtitle}
              </p>
            )}

            <RichText
              data={tableBody}
              overrideOptions={RICH_TEXT_OVERRIDES}
              overrideMarkOptions={{
                [MARKS.CODE]: (children) => (
                  <a
                    className="text-navy no-underline"
                    id={`${tableId}-footnote-ref-${children}`.trim()}
                    href={`#${tableId}-footnote-${children}`.trim()}
                    aria-describedby={`${tableId}-footnotes`}
                    role="doc-noteref"
                  >
                    <sup>{children}</sup>
                  </a>
                )
              }}
            />

            {tableFootnotes && (
              <div id={`${tableId}-footnotes`}>
                <RichText
                  data={tableFootnotes}
                  overrideOptions={RICH_TEXT_OVERRIDES}
                  overrideMarkOptions={{
                    [MARKS.CODE]: (children) => (
                      <a
                        className="text-navy no-underline"
                        id={`${tableId}-footnote-${children}`.trim()}
                        href={`#${tableId}-footnote-ref-${children}`.trim()}
                      >
                        <sup>{children}</sup>
                      </a>
                    )
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </ScrollReveal>
  );
}
